import { useState, useEffect, useCallback } from 'react';
import type { WidgetPosition } from '../types/events';
import { directionForLanguage, type Direction } from '../i18n/useDirection';

/**
 * Chat settings received from the API.
 * Structure matches the PublicWidgetView response.
 */
export interface ChatSettings {
  id: string;
  routerId: number;
  label: string;
  settings: {
    language: string;
    primaryColor: string;
    secondaryColor: string;
    accentColor: string;
    backgroundColor: string;
    textColor: string;
    /** Text inside the visitor's own bubbles; white unless the club picked something else. */
    userTextColor: string;
    salesStarter: string;
    profilePictureUrl: string;
    chatbotLogoUrl: string;
    buttonStyle: 'icon' | 'text' | 'avatar_text' | 'avatar';
    buttonText: string;
    conversationStarters: string[];
    logoStyle: 'square_rounded' | 'horizontal_broad';
    logoFit: 'cover' | 'contain';
  };
  layout: {
    position: WidgetPosition;
    paddingX: number;
    paddingY: number;
    direction: Direction;
    allowDirectionChange: boolean;
  };
  messages: {
    welcome: string;
    fallback: string;
    goodbye: string;
    error: string;
  };
}

/**
 * Raw API response shape (snake_case from Django).
 */
interface ApiWidgetResponse {
  id: string;
  router_id: number;
  label: string;
  language: string;
  appearance: {
    primary_color: string;
    secondary_color: string;
    accent_color: string;
    background_color: string;
    text_color: string;
    // Optional: a backend from before this setting existed does not send it.
    user_text_color?: string;
  };
  messages: {
    welcome: string;
    fallback: string;
    goodbye: string;
    error: string;
  };
  sales_starter: string;
  profile_picture_url?: string;
  chatbot_logo_url?: string;
  button_style?: string;
  button_text?: string;
  conversation_starters?: string[];
  logo_style?: string;
  logo_fit?: string;
  layout?: {
    position?: string;
    padding_x?: number;
    padding_y?: number;
    direction?: string;
    allow_direction_change?: boolean;
  };
}

/**
 * Hook return type for useRouterSettings.
 */
export interface UseRouterSettingsReturn {
  /** The chat settings, or null if not yet loaded */
  settings: ChatSettings | null;
  /** Whether the settings are currently being fetched */
  isLoading: boolean;
  /** Any error that occurred during fetch */
  error: string | null;
  /** Refetch the settings */
  refetch: () => Promise<void>;
}

const VALID_POSITIONS: readonly WidgetPosition[] = ['bottom-right', 'bottom-left', 'top-right', 'top-left'] as const;
const VALID_DIRECTIONS: readonly Direction[] = ['ltr', 'rtl'] as const;

function normalizeLayout(
  raw: ApiWidgetResponse['layout'],
  language: string | undefined
): ChatSettings['layout'] {
  const position = raw?.position;
  const paddingX = typeof raw?.padding_x === 'number' ? raw.padding_x : 16;
  const paddingY = typeof raw?.padding_y === 'number' ? raw.padding_y : 16;
  return {
    position: VALID_POSITIONS.includes(position as WidgetPosition)
      ? (position as WidgetPosition)
      : 'bottom-right',
    paddingX,
    paddingY,
    direction: VALID_DIRECTIONS.includes(raw?.direction as Direction)
      ? (raw!.direction as Direction)
      : directionForLanguage(language),
    allowDirectionChange: raw?.allow_direction_change === true,
  };
}

/**
 * Convert snake_case API response to camelCase ChatSettings.
 */
function transformApiResponse(response: ApiWidgetResponse): ChatSettings {
  const logoStyle: ChatSettings['settings']['logoStyle'] =
    ['square_rounded', 'horizontal_broad'].includes(response.logo_style || '')
      ? response.logo_style as ChatSettings['settings']['logoStyle']
      : 'square_rounded';
  const logoFit: ChatSettings['settings']['logoFit'] =
    ['cover', 'contain'].includes(response.logo_fit || '')
      ? response.logo_fit as ChatSettings['settings']['logoFit']
      : (logoStyle === 'horizontal_broad' ? 'contain' : 'cover');
  return {
    id: response.id,
    routerId: response.router_id,
    label: response.label,
    settings: {
      language: response.language,
      primaryColor: response.appearance.primary_color,
      secondaryColor: response.appearance.secondary_color,
      accentColor: response.appearance.accent_color,
      backgroundColor: response.appearance.background_color,
      textColor: response.appearance.text_color,
      userTextColor: response.appearance.user_text_color || '#FFFFFF',
      salesStarter: response.sales_starter,
      profilePictureUrl: response.profile_picture_url || '',
      chatbotLogoUrl: response.chatbot_logo_url || '',
      buttonStyle: (['icon', 'text', 'avatar_text', 'avatar'].includes(response.button_style || '')
        ? response.button_style as 'icon' | 'text' | 'avatar_text' | 'avatar'
        : 'icon'),
      buttonText: response.button_text || '',
      // Blank chips render as empty bubbles above the launcher, so drop them here as well as
      // server-side: a widget served by an older backend must not show them either.
      conversationStarters: (response.conversation_starters || [])
        .filter((text) => typeof text === 'string' && text.trim())
        .map((text) => text.trim()),
      logoStyle,
      logoFit,
    },
    layout: normalizeLayout(response.layout, response.language),
    messages: {
      welcome: response.messages.welcome,
      fallback: response.messages.fallback,
      goodbye: response.messages.goodbye,
      error: response.messages.error,
    },
  };
}

/**
 * Hook for fetching widget settings from the public API.
 *
 * Fetches settings from GET /api/public/widgets/{channelId}/ and transforms
 * the response to use camelCase property names.
 *
 * Also supports legacy GET /api/public/routers/{routerId}/ for backward compat.
 *
 * @param apiHost - The API host URL (e.g., 'http://localhost:8000')
 * @param idOrRouterId - The channel ID (UUID) or legacy router ID (number)
 * @returns Settings state, loading status, error state, and refetch function
 */
export function useRouterSettings(apiHost: string, idOrRouterId: string): UseRouterSettingsReturn {
  const [settings, setSettings] = useState<ChatSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Detect if this is a UUID (channel ID) or numeric (legacy router ID)
  const isChannelId = idOrRouterId.includes('-');

  /**
   * Fetch chat settings from the API.
   */
  const fetchSettings = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const url = isChannelId
        ? `${apiHost}/api/public/widgets/${idOrRouterId}/`
        : `${apiHost}/api/public/routers/${idOrRouterId}/`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Widget not found or not active');
        }
        throw new Error(`Failed to fetch settings: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      // Handle both new widget response and legacy router response
      let transformed: ChatSettings;
      if (data.appearance) {
        // New widget response format
        transformed = transformApiResponse(data as ApiWidgetResponse);
      } else {
        // Legacy router response format
        transformed = {
          id: String(data.id),
          routerId: data.id,
          label: data.label,
          settings: {
            language: data.settings?.language || 'en',
            primaryColor: data.settings?.primary_color || '#000000',
            secondaryColor: data.settings?.secondary_color || '#FFFFFF',
            accentColor: data.settings?.accent_color || '#007BFF',
            backgroundColor: data.settings?.background_color || '#FFFFFF',
            textColor: data.settings?.text_color || '#333333',
            userTextColor: data.settings?.user_text_color || '#FFFFFF',
            salesStarter: data.settings?.sales_starter || '',
            profilePictureUrl: data.settings?.profile_picture_url || '',
            chatbotLogoUrl: '',
            buttonStyle: 'icon',
            buttonText: '',
            conversationStarters: [],
            logoStyle: 'square_rounded',
            logoFit: 'cover',
          },
          layout: {
            position: 'bottom-right',
            paddingX: 16,
            paddingY: 16,
            direction: directionForLanguage(data.settings?.language),
            allowDirectionChange: false,
          },
          messages: data.messages || {},
        };
      }

      setSettings(transformed);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch chat settings';
      setError(message);
      console.error('[ClubPlannerWidget] Failed to fetch chat settings:', err);
    } finally {
      setIsLoading(false);
    }
  }, [apiHost, idOrRouterId, isChannelId]);

  // Fetch settings on mount and when dependencies change
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return {
    settings,
    isLoading,
    error,
    refetch: fetchSettings,
  };
}

export default useRouterSettings;
